import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { isStudyArtifactAttentionTarget, isStudyQuestionnaireAttentionTarget, type StudyAttentionTarget, type QuestionnaireThreadDraftListItem, type StudyQuestionnaireAttentionTarget, type StudyArtifactAttentionReceipt, type StudyArtifactAttentionTarget, type ThreadArtifactDto, type QuickStudyDraftView } from '@digital-twins/shared-types'

interface OriginRequest {
  threadToken: string
  studyToken: string
  originMessageToken: string
}

/** An authorized catalog row proves a source exists, not an exact canonical tip. */
export function hasStudyFindingsSource(request: OriginRequest, artifacts: ReadonlyArray<ThreadArtifactDto>): boolean {
  return artifacts.some((item) => item.kind === 'findings' && item.completionDelivery === 'origin_card'
    && item.sourceStudyToken === request.studyToken && item.originMessageToken === request.originMessageToken)
}

/** A URL names an origin; only a receipt or saved-findings GET names its exact tip. */
export function resolveStudyOrigin(
  request: OriginRequest,
  receipts: ReadonlyArray<StudyArtifactAttentionReceipt>,
  artifacts: ReadonlyArray<ThreadArtifactDto>,
  confirmedTarget: StudyArtifactAttentionTarget | null = null,
): StudyArtifactAttentionTarget | null {
  const matches = (target: StudyArtifactAttentionTarget) =>
    target.threadToken === request.threadToken && target.studyToken === request.studyToken
    && target.originMessageToken === request.originMessageToken
  const receipt = receipts.find((item) => isStudyArtifactAttentionTarget(item.target) && matches(item.target))
  if (receipt && isStudyArtifactAttentionTarget(receipt.target)) return receipt.target
  return confirmedTarget && matches(confirmedTarget) && hasStudyFindingsSource(request, artifacts) ? confirmedTarget : null
}

/** Draft readiness uses the same authorized source-message binding, before a run exists. */
export function resolveStudyDraftOrigin(
  request: { threadToken: string; draftToken: string; originMessageToken: string; draftRevision?: number },
  receipts: ReadonlyArray<StudyArtifactAttentionReceipt>,
  drafts: ReadonlyArray<QuestionnaireThreadDraftListItem>,
): StudyQuestionnaireAttentionTarget | null {
  const draft = drafts.find((item) => item.id === request.draftToken && item.presentation === 'quick'
    && item.threadOrigin.sourceThreadToken === request.threadToken
    && item.threadOrigin.originMessageToken === request.originMessageToken)
  if (draft) return { ...request, stage: 'questions', draftRevision: draft.revision }
  const receipt = receipts.find((item) => isStudyQuestionnaireAttentionTarget(item.target)
    && item.target.threadToken === request.threadToken && item.target.draftToken === request.draftToken
    && item.target.originMessageToken === request.originMessageToken)
  return receipt && isStudyQuestionnaireAttentionTarget(receipt.target) ? receipt.target : null
}

/** Resolve origin-only Quick links from this thread's authorized, authored catalog. */
export function resolveNativeQuickOrigin(
  originMessageToken: string,
  drafts: ReadonlyArray<QuickStudyDraftView>,
): QuickStudyDraftView | undefined {
  return drafts.find(draft => draft.originMessageToken === originMessageToken && draft.instrument !== null);
}

interface OriginNavigationOptions {
  container: RefObject<HTMLDivElement>
  threadToken: string
  requestedOrigin: string
  requestedStudy: string
  target: StudyAttentionTarget | null
  loading: boolean
  navigationKey: string
  layoutRevision: number
  messageTokens: ReadonlyArray<string>
}

/** Explicit navigation wins arrival scrolling; readiness arriving alone never navigates. */
export function useThreadStudyOrigin(options: OriginNavigationOptions) {
  const { container, threadToken, requestedOrigin, requestedStudy, target, loading, navigationKey, layoutRevision, messageTokens } = options
  const key = requestedOrigin && requestedStudy ? `${navigationKey}:${threadToken}:${requestedStudy}:${requestedOrigin}` : ''
  // A history entry can be visited again after leaving it. Its router key stays
  // the same, while each explicit visit must get a fresh navigation hold.
  const visit = useRef({ key, sequence: 0 })
  if (visit.current.key !== key) visit.current = { key, sequence: visit.current.sequence + 1 }
  const visitKey = `${visit.current.sequence}:${key}`
  const messageIdentity = messageTokens.join('\0')
  const jumped = useRef('')
  const [released, setReleased] = useState('')
  const [awayFromBottom, setAwayFromBottom] = useState(false)
  const holdOrigin = Boolean(key && released !== visitKey && (loading || target))
  const holdOriginRef = useRef(holdOrigin)
  useLayoutEffect(() => { holdOriginRef.current = holdOrigin }, [holdOrigin])

  useLayoutEffect(() => {
    if (!holdOrigin || !target || loading || jumped.current === visitKey) return
    const scroller = container.current
    if (!scroller) return
    const element = Array.from(scroller.querySelectorAll<HTMLElement>('[data-thread-message-token]'))
      .find((item) => item.dataset.threadMessageToken === target.originMessageToken)
    if (!element) return
    const top = Math.max(0, scroller.scrollTop + element.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 64)
    scroller.scrollTo({ top, behavior: 'instant' })
    jumped.current = visitKey
    setAwayFromBottom(scroller.scrollHeight - top - scroller.clientHeight > 80)
  }, [container, holdOrigin, visitKey, target, loading, layoutRevision, messageIdentity])

  useEffect(() => {
    const scroller = container.current
    if (!scroller) return
    const update = () => setAwayFromBottom(scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 80)
    scroller.addEventListener('scroll', update, { passive: true })
    update()
    return () => scroller.removeEventListener('scroll', update)
  }, [container, threadToken, loading, layoutRevision])

  const releaseOrigin = useCallback(() => setReleased(visitKey), [visitKey])

  const returnToLatest = useCallback(() => {
    releaseOrigin()
    const scroller = container.current
    scroller?.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' })
  }, [container, releaseOrigin])

  const showMessage = useCallback((messageToken: string) => {
    releaseOrigin()
    const scroller = container.current
    const element = scroller && Array.from(scroller.querySelectorAll<HTMLElement>('[data-thread-message-token]'))
      .find(item => item.dataset.threadMessageToken === messageToken)
    // Smooth glide to the message unless the reader asked for reduced motion.
    const prefersReducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (scroller && element) scroller.scrollTo({top: Math.max(0, scroller.scrollTop + element.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 24), behavior: prefersReducedMotion ? 'instant' : 'smooth'})
  }, [container, releaseOrigin])

  return { holdOrigin, holdOriginRef, awayFromBottom, returnToLatest, releaseOrigin, showMessage }
}
